import Grid from '@mui/material/Grid';
import React, { useCallback, useEffect, useMemo } from 'react';
import ContactNameField from './ContactNameField';
import ContactPhoneField from './ContactPhoneField';
import ContactDateField from './ContactDateField';
import IsMainContact from './IsMainContact';
import { useFormikContext } from 'formik';
import { useGetContactQuery } from '@/features/contacts/contactsApi';
import ContactTextField from './ContactTextField';
import { useClientSchemaContext } from '@/context/clientSchemaContext';
import { getGridLayout } from '@/utils/fieldLayout';
import { VIEW_TYPES } from '@/features/config/helper';
import RemoveContactButton from './RemoveContactButton';

const Contact = ({ fields, index, disabled }) => {
	const { values, setFieldValue } = useFormikContext();
	const { view, variant, fieldDensity } = useClientSchemaContext();

	const existingContactDocId = values.contacts[index]?.Id;
	const existingContactQuery = useGetContactQuery(existingContactDocId, {
		skip: !existingContactDocId,
	});

	const existingContact = existingContactQuery.currentData;

	useEffect(() => {
		if (existingContact) {
			const existingContacts = values.contacts.slice();
			const newContacts = existingContacts.map((c, i) => {
				if (i !== index) return c;
				const {
					FirstName,
					LastName,
					Email,
					Telephone,
					Telephone2,
					BirthDate,
					Salutation,
					JobTitle,
					Mobile,
					Fax,
				} = existingContact;

				console.log('Existing contact data', existingContact);
				return {
					...c,
					...(FirstName && { FirstName }),
					...(LastName && { LastName }),
					...(Email && { Email }),
					...(Telephone && { Telephone }),
					...(Telephone2 && { Telephone2 }),
					...(BirthDate && { BirthDate }),
					...(Salutation && { Salutation }),
					...(JobTitle && { JobTitle }),
					...(Mobile && { Mobile }),
					...(Fax && { Fax }),
				};
			});
			setFieldValue('contacts', newContacts);
		}
	}, [existingContact]);

	const contactFields = useMemo(() => {
		const defaultFields = fields.map((f) => ({
			...f,
			name: `contacts[${index}].${f.name}`,
			fieldName: f.name,
		}));
		// We only want to see the main contact on the create screen
		return view === VIEW_TYPES.create
			? defaultFields
			: [
					{
						fieldName: 'isMainContact',
						name: `contacts[${index}].isMainContact`,
						layout: 'compact',
					},
				].concat(defaultFields);
	}, [fields, index, view]);

	const sizeSchema = useMemo(
		() => getGridLayout(contactFields, fieldDensity),
		[contactFields, fieldDensity],
	);

	const getField = useCallback(
		(field) => {
			const size = sizeSchema[field.name];
			switch (field.fieldName) {
				case 'Contact':
					return (
						<Grid {...{ size }} key={field.name}>
							<ContactNameField {...{ field, index, disabled }} />
						</Grid>
					);
				case 'Telephone':
				case 'Telephone2':
				case 'Mobile':
				case 'Fax':
					return (
						<Grid {...{ size }} key={field.name}>
							<ContactPhoneField {...{ field, index, disabled }} />
						</Grid>
					);
				case 'BirthDate':
					return (
						<Grid {...{ size }} key={field.name}>
							<ContactDateField {...{ field, index, disabled }} />
						</Grid>
					);
				case 'isMainContact':
					return (
						<Grid {...{ size }} key={field.name}>
							<IsMainContact {...{ index, disabled }} />
						</Grid>
					);
				default:
					return (
						<Grid {...{ size }} key={field.name}>
							<ContactTextField {...{ field, index, disabled }} />
						</Grid>
					);
			}
		},
		[contactFields, index, disabled, sizeSchema],
	);

	return (
		<Grid container rowSpacing={1} columnSpacing={2}>
			{contactFields.map((f) => getField(f))}
			{view !== VIEW_TYPES.create && !variant && (
				<Grid size={12} display='flex' justifyContent={'flex-end'} alignItems={'center'}>
					<RemoveContactButton index={index} />
				</Grid>
			)}
		</Grid>
	);
};

export const EditContact = ({ fields, index, disabled }) => {
	const { values, setFieldValue } = useFormikContext();
	const view = VIEW_TYPES.update;

	const existingContactDocId = values.contacts[index]?.Id;
	const existingContactQuery = useGetContactQuery(existingContactDocId, {
		skip: !existingContactDocId,
	});
	const existingContact = existingContactQuery.currentData;

	useEffect(() => {
		if (existingContact) {
			console.log('Existing contact', existingContact);
			const existingContacts = values.contacts.slice();
			const newContacts = existingContacts.map((c, i) => {
				if (i !== index) return c;
				const {
					FirstName,
					LastName,
					Email,
					Telephone,
					Telephone2,
					BirthDate,
					Salutation,
					JobTitle,
					Mobile,
					Fax,
				} = existingContact;

				return {
					...c,
					...(FirstName && { FirstName }),
					...(LastName && { LastName }),
					...(Email && { Email }),
					...(Telephone && { Telephone }),
					...(Telephone2 && { Telephone2 }),
					...(BirthDate && { BirthDate }),
					...(Salutation && { Salutation }),
					...(JobTitle && { JobTitle }),
					...(Mobile && { Mobile }),
					...(Fax && { Fax }),
				};
			});
			setFieldValue('contacts', newContacts);
		}
	}, [existingContact]);

	const contactFields = useMemo(() => {
		const defaultFields = fields.map((f) => ({
			...f,
			name: `contacts[${index}].${f.name}`,
			fieldName: f.name,
		}));
		return defaultFields.sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
	}, [fields, index, view]);

	const size = { xs: 12 };

	const getField = useCallback(
		(field) => {
			switch (field.fieldName) {
				case 'Contact':
					return (
						<Grid {...{ size }} key={field.name}>
							<ContactNameField {...{ field, index, disabled }} />
						</Grid>
					);
				case 'Telephone':
				case 'Telephone2':
				case 'Mobile':
				case 'Fax':
					return (
						<Grid {...{ size }} key={field.name}>
							<ContactPhoneField {...{ field, index, disabled }} />
						</Grid>
					);
				case 'BirthDate':
					return (
						<Grid {...{ size }} key={field.name}>
							<ContactDateField {...{ field, index, disabled }} />
						</Grid>
					);
				default:
					return (
						<Grid {...{ size }} key={field.name}>
							<ContactTextField {...{ field, index, disabled }} />
						</Grid>
					);
			}
		},
		[contactFields, index, disabled, size],
	);

	return (
		<Grid container rowSpacing={1} columnSpacing={2}>
			{contactFields.map((f) => getField(f))}
		</Grid>
	);
};

export default Contact;
